/**
 * Chart colours — the one place they live.
 *
 * Charts render on a canvas (Plotly) or compute colours in JS (Recharts), where
 * `var(--token)` cannot be resolved. So these are plain hexes, kept in step with
 * `tokens.css` by hand. Change a colour here and every chart follows; change one
 * in `tokens.css` and check whether its twin below needs the same edit.
 *
 * The categorical order is fixed. Assign series by position and never cycle or
 * generate a hue — a ninth series folds into "Other" or gets its own chart, so
 * a filter that drops a series never repaints the survivors.
 */

/** Ink and furniture, matched to the app's text and border tokens. */
export const chartInk = {
  /** Axis labels, tick text. */
  axis: '#7e8ca0',
  /** Series labels, legend text. */
  label: '#48596f',
  /** Titles and emphasised values. */
  strong: '#0a1c31',
  /** Grid lines — recessive by design. */
  grid: '#e2e8f1',
  /** The plotting surface. */
  surface: '#ffffff',
  /** Page ground behind a chart card. */
  ground: '#eef1f6',
} as const;

/** Assign in order. Never cycle past the end. */
export const chartSeries = [
  '#1b4a86', // navy — the first series is the brand
  '#2f8b5b', // green
  '#b06f10', // amber
  '#6b4fa8', // violet
  '#0f7f8c', // teal
  '#a23f6d', // plum
  '#4a6070', // slate
  '#8a6a2f', // bronze
] as const;

/**
 * Sector composition — the one categorical chart whose series count is data-driven
 * (a portfolio can hold 20+ sectors), so the eight-colour `chartSeries` rule cannot
 * hold. Twenty fixed, mutually distinct hues, deliberately leaving out the brand
 * navy and the positive green. Read through `sectorColor`, never by index.
 */
export const chartSectors = [
  '#b06f10', // amber
  '#6b4fa8', // violet
  '#0f7f8c', // teal
  '#a23f6d', // plum
  '#c2553a', // terracotta
  '#3f7fbf', // sky
  '#8a6a2f', // bronze
  '#9c4fb0', // orchid
  '#4a6070', // slate
  '#d08c2e', // ochre
  '#b0475a', // rose
  '#5d6fb8', // periwinkle
  '#8c5a3c', // brown
  '#6a9fb5', // steel
  '#c46f9b', // pink
  '#4f8f8f', // sea
  '#a88b3a', // mustard
  '#7b6a9e', // lavender
  '#d0694a', // coral
  '#56769c', // denim
] as const;

/**
 * Colour for the idx-th sector. Past the fixed list it steps round the hue wheel
 * by the golden angle, so every sector keeps a colour of its own however many
 * there are — a cycled palette would paint two sectors the same.
 */
export const sectorColor = (idx: number): string => {
  if (idx < chartSectors.length) return chartSectors[idx];
  const hue = Math.round((idx * 137.508) % 360);
  return `hsl(${hue}, 42%, 48%)`;
};

/** Direction, not identity. Green is a gain, red a loss — never a category. */
export const chartSemantic = {
  positive: '#15784f',
  negative: '#bd2f28',
  warning: '#8f5e09',
  neutral: '#4c5d74',
} as const;

/** One hue, light to dark, for magnitude (heatmaps, weight shading). */
export const chartSequential = [
  '#e9eef6',
  '#c6d5e8',
  '#9db7d7',
  '#6f95c3',
  '#3f6fa8',
  '#1b4a86',
] as const;

/** Two poles and a neutral middle, for values that cross zero. */
export const chartDiverging = {
  negative: '#bd2f28',
  neutral: '#e7ecf3',
  positive: '#15784f',
} as const;

/** Plotly layout defaults, so every Plotly chart starts from the same place. */
export const plotlyLayout = {
  paper_bgcolor: chartInk.surface,
  plot_bgcolor: chartInk.surface,
  font: { family: 'Figtree, system-ui, sans-serif', size: 12, color: chartInk.label },
  xaxis: { gridcolor: chartInk.grid, zerolinecolor: chartInk.grid, linecolor: chartInk.grid },
  yaxis: { gridcolor: chartInk.grid, zerolinecolor: chartInk.grid, linecolor: chartInk.grid },
  colorway: [...chartSeries],
} as const;
